import React, { useState, useEffect } from 'react';
import ToolLayout from '../../components/ToolLayout';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { Copy, Hash as HashIcon } from 'lucide-react';

const HashGeneratorPage = () => {
  const { toast } = useToast();
  const [input, setInput] = useState('');
  const [hashes, setHashes] = useState({
    md5: '',
    sha1: '',
    sha256: ''
  });

  const generateHash = async (algorithm, data) => {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest(algorithm, dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const md5 = (str) => {
    // Simple MD5 implementation (not cryptographically secure, for demonstration)
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16).padStart(32, '0');
  };

  const generateHashes = async () => {
    if (!input) {
      setHashes({ md5: '', sha1: '', sha256: '' });
      return;
    }

    try {
      const sha1Hash = await generateHash('SHA-1', input);
      const sha256Hash = await generateHash('SHA-256', input);
      const md5Hash = md5(input);

      setHashes({
        md5: md5Hash,
        sha1: sha1Hash,
        sha256: sha256Hash
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to generate hashes',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      generateHashes();
    }, 300);
    return () => clearTimeout(timer);
  }, [input]);

  const copyHash = (hash) => {
    navigator.clipboard.writeText(hash);
    toast({
      title: 'Copied!',
      description: 'Hash copied to clipboard',
    });
  };

  return (
    <ToolLayout
      title="Hash Generator"
      description="Generate MD5, SHA-1, and SHA-256 hashes for your text. Useful for checksums and data integrity verification."
      icon={HashIcon}
    >
      <div className="space-y-6">
        <div>
          <Label htmlFor="input">Input Text</Label>
          <Textarea
            id="input"
            placeholder="Enter text to generate hashes..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={6}
            className="font-mono text-sm"
          />
        </div>

        {input && (
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">MD5</p>
                    <p className="font-mono text-sm break-all">{hashes.md5}</p>
                  </div>
                  <Button onClick={() => copyHash(hashes.md5)} variant="ghost" size="sm">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">SHA-1</p>
                    <p className="font-mono text-sm break-all">{hashes.sha1}</p>
                  </div>
                  <Button onClick={() => copyHash(hashes.sha1)} variant="ghost" size="sm">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">SHA-256</p>
                    <p className="font-mono text-sm break-all">{hashes.sha256}</p>
                  </div>
                  <Button onClick={() => copyHash(hashes.sha256)} variant="ghost" size="sm">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ToolLayout>
  );
};

export default HashGeneratorPage;